/**
 * Dynamic page titles.
 *
 * A route's title lives in `meta.pageTitle` (see the `navigation/routes.*.js` files).
 * It can be a plain title ("Edit Client"), a title path ("Clients > Edit Client"), and
 * it can contain `{placeholder}` tokens that the page fills in at runtime:
 *
 *		meta: { pageTitle: "Edit Client - {clientName}" }
 *
 * The page then supplies the value, and both the on-page header (PrimaryLayout) and the
 * browser tab/HTML <title> update together:
 *
 *		import { setPageTitleValues } from "/navigation/page-title.js";
 *
 *		setPageTitleValues({ clientName: responseData.Client.Name });
 *
 * A placeholder with no value yet (the API call hasn't returned, or the value is blank)
 * is dropped along with the separator in front of it, so the example above reads
 * "Edit Client" while loading rather than showing the raw "{clientName}" token.
 *
 * To replace the route's title outright, use `setPageTitle("Whatever")` — it accepts the
 * same title-path and placeholder syntax.
 *
 * Both the values and the replacement are cleared on every navigation (see
 * `installPageTitle()`, wired up in app.js), so a title never leaks into the next page.
 *
 * NOTE: `route.meta` is a plain, non-reactive object. Mutating `route.meta.pageTitle`
 * from inside a mounted component does not propagate to the layout — Vue Router only
 * reacts to the route object being replaced wholesale on navigation. That's why the
 * values/replacement below are held in refs rather than written back onto the route.
 */

import { isRef, ref, watch, watchEffect } from "vue";


//-- Prefix for the browser tab / HTML <title>.
const applicationName = "Lotus AI";

//-- Separates the parts of a title path ("Clients > Edit Client") in `meta.pageTitle`.
export const titlePathSeparator = " > ";

//-- A `{placeholder}` token, optionally preceded by a joining character
//   ("Edit Client - {clientName}"). Capturing the joiner lets an unresolved
//   placeholder take its separator with it instead of leaving "Edit Client -".
const placeholderPattern = /(\s*[-–—:|,]\s*)?\{\s*([\w.]+)\s*\}/g;


//-- Reactive state, reset on every navigation.
const titleReplacement = ref(null);		// Replaces `meta.pageTitle` entirely.
const titleValues = ref({});			// Values for the `{placeholder}` tokens.






//-- Methods

/**
 * Replace the current page's title entirely, ignoring `meta.pageTitle`.
 * The title may itself contain a title path and `{placeholder}` tokens.
 */
export function setPageTitle(title)
{
	titleReplacement.value = title ? title : null;
}



/**
 * Supply values for the `{placeholder}` tokens in the current page's title.
 * Merges with anything already set, so a page can fill placeholders in stages
 * (e.g. one per API call) — `setPageTitleValues({ clientName: "Acme" })`.
 */
export function setPageTitleValues(values)
{
	titleValues.value = { ...titleValues.value, ...(values ? values : {}) };
}



/**
 * Drop the title replacement and all placeholder values, returning the page to
 * whatever `meta.pageTitle` says. Called automatically on every navigation.
 */
export function clearPageTitle()
{
	titleReplacement.value = null;
	titleValues.value = {};
}



/**
 * Resolve the `{placeholder}` tokens in a single piece of title text.
 * Also usable for other route-driven text, e.g. `meta.breadcrumbLabel`.
 */
export function resolvePageTitleText(text)
{
	if (!text) return "";

	const values = titleValues.value;

	const resolved = String(text).replace(placeholderPattern, (match, joiner, key) =>
	{
		const value = values[key];

		// No value (yet): drop the placeholder and its joining character, so the title
		// degrades to the static part rather than displaying the raw token.
		if (value === undefined || value === null || String(value).trim() === "") return "";

		return (joiner ? joiner : "") + String(value);
	});

	return tidyTitleText(resolved);
}



/**
 * The raw (unresolved) title template for a route: the replacement set by the page
 * when there is one, otherwise the route's own `meta.pageTitle`.
 */
export function pageTitleTemplate(route)
{
	if (titleReplacement.value) return titleReplacement.value;

	return route && route.meta && route.meta.pageTitle ? route.meta.pageTitle : "";
}



/**
 * The resolved parts of a route's title path, ready to display.
 * Parts left empty by unresolved placeholders are dropped.
 */
export function resolvePageTitleSegments(route)
{
	return String(pageTitleTemplate(route))
		.split(titlePathSeparator)
		.map((segment) => resolvePageTitleText(segment))
		.filter((segment) => segment !== "");
}



/**
 * A route's fully resolved title as a single string ("Clients > Edit Client - Acme").
 */
export function resolvePageTitle(route)
{
	return resolvePageTitleSegments(route).join(titlePathSeparator);
}



/**
 * The browser tab / HTML <title> for a route.
 */
export function buildDocumentTitle(route)
{
	const title = resolvePageTitle(route);

	return title ? applicationName + ": " + title : applicationName;
}



/**
 * Keeps the page title in step with reactive page state — use this instead of calling
 * `setPageTitleValues()` by hand when the title follows something that changes after it
 * is first set (a form field the user is typing into, a record reloaded in place).
 *
 * Pass a getter (or a ref) returning either:
 *   - an object of placeholder values:  usePageTitle(() => ({ clientName: form.Name }));
 *   - a string, to replace the title:   usePageTitle(() => report.value?.Name);
 *
 * Call it from `<script setup>` — the watcher stops with the component.
 */
export function usePageTitle(source)
{
	const apply = () =>
	{
		const value = typeof source === "function" ? source() : (isRef(source) ? source.value : source);

		if (value === null || value === undefined) return;

		if (typeof value === "string") setPageTitle(value);
		else setPageTitleValues(value);
	};

	// A plain (non-reactive) object can't be watched — just apply it once.
	if (typeof source !== "function" && !isRef(source))
	{
		apply();
		return;
	}

	watch(source, apply, { immediate: true });
}



/**
 * Wires the page title into the router. Called once from app.js.
 */
export function installPageTitle(router)
{
	// A dynamic title belongs to the page that set it. Clear on `afterEach` rather than
	// `beforeEach` so a cancelled navigation doesn't wipe the current page's title.
	// Page components render after this runs, so a title set in setup()/init() survives.
	router.afterEach(() => clearPageTitle());

	// Browser tab. `router.currentRoute` is reactive, so this re-runs on navigation as
	// well as on every setPageTitle()/setPageTitleValues() call.
	watchEffect(() => { document.title = buildDocumentTitle(router.currentRoute.value); });
}



/**
 * Tidy up after placeholder substitution: collapse whitespace and strip any joining
 * characters left dangling at either end ("Edit Client - " -> "Edit Client").
 */
function tidyTitleText(text)
{
	return text
		.replace(/\s+/g, " ")
		.replace(/^[\s\-–—:|,]+/, "")
		.replace(/[\s\-–—:|,]+$/, "")
		.trim();
}
